import React, { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import {
  Search, RefreshCw, Download, Copy, Check, ChevronLeft, Building2, Mail, Users,
  FileText, ShieldCheck, AlertTriangle, History, Radar, X, CreditCard, ExternalLink, Globe, Link2,
} from "lucide-react";
import { Link } from "react-router-dom";
import AdminPageShell from "../components/AdminPageShell";
import { API } from "../apiBase";

const B = `${API}/admin/demandes-mss`;

const STATUT_STYLE = {
  recue: "bg-sky-50 text-sky-700 border-sky-200",
  structure_a_verifier: "bg-amber-50 text-amber-800 border-amber-200",
  en_attente_quota: "bg-amber-50 text-amber-800 border-amber-200",
  attente_paiement: "bg-orange-50 text-orange-700 border-orange-200",
  en_cours: "bg-indigo-50 text-indigo-700 border-indigo-200",
  creee: "bg-violet-50 text-violet-700 border-violet-200",
  publiee: "bg-emerald-50 text-emerald-700 border-emerald-200",
  refusee: "bg-slate-100 text-slate-600 border-slate-200",
  annulee: "bg-slate-100 text-slate-500 border-slate-200",
};
const FILTRES = [
  ["a_traiter", "À traiter"], ["attente_paiement", "Paiement en attente"], ["creee", "Créées chez Maincare"],
  ["publiee", "Publiées"], ["refusee", "Refusées"], ["annulee", "Annulées"], ["", "Toutes"],
];
const PROVENANCES = [["", "Toutes les provenances"], ["site", "Site · Commander ma boîte"], ["ohmyvue", "Lien OhMyVue"]];
const PAIEMENT_STYLE = {
  active: "text-emerald-700", legacy: "text-slate-500", pending_payment: "text-orange-700",
  past_due: "text-red-700", cancelled: "text-slate-500",
};
const OUVERTS = ["recue", "structure_a_verifier", "en_attente_quota", "attente_paiement"];

const jeton = () => {
  try { return localStorage.getItem("mc_token") || ""; } catch (e) { return ""; }
};
const date = (v) => {
  if (!v) return "—";
  const d = new Date(/Z|[+-]\d\d:\d\d$/.test(v) ? v : `${v}Z`);
  return d.toLocaleString("fr-FR", { dateStyle: "short", timeStyle: "short" });
};
const erreur = (e) => e?.response?.data?.detail || "Action impossible pour le moment.";

function Pastille({ statut, libelles }) {
  return (
    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full border text-[11.5px] font-medium whitespace-nowrap ${STATUT_STYLE[statut] || STATUT_STYLE.refusee}`}>
      {libelles?.[statut] || statut}
    </span>
  );
}

function BoutonCopier({ texte, libelle = "Copier" }) {
  const [ok, setOk] = useState(false);
  if (!texte) return null;
  const copier = async () => {
    try {
      await navigator.clipboard.writeText(String(texte));
      setOk(true);
      setTimeout(() => setOk(false), 1400);
    } catch (e) { /* presse-papiers refusé */ }
  };
  return (
    <button type="button" onClick={copier} title={`Copier : ${texte}`}
      className={`inline-flex items-center gap-1 px-2 py-1 rounded-md border text-[11.5px] transition focus:outline-none focus-visible:ring-2 focus-visible:ring-sky-400 ${ok ? "border-emerald-300 bg-emerald-50 text-emerald-700" : "border-[#E2E8F0] bg-white text-[#475569] hover:border-sky-400 hover:text-[#0F2B5B]"}`}>
      {ok ? <Check size={12} /> : <Copy size={12} />}{ok ? "Copié" : libelle}
    </button>
  );
}

function Provenance({ it, grand = false }) {
  const site = it.source === "site";
  const Icone = site ? Globe : Link2;
  return (
    <span className="inline-flex flex-col gap-0.5">
      <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-md border font-medium whitespace-nowrap w-fit ${grand ? "text-[12px]" : "text-[11.5px]"} ${site ? "bg-[#EFF6FF] border-[#BFDBFE] text-[#1D4ED8]" : "bg-teal-50 border-teal-200 text-teal-800"}`}>
        <Icone size={12} />{site ? "Site MetaConnect" : "Lien OhMyVue"}
      </span>
      {site && it.paiement_libelle && (
        <span className={`inline-flex items-center gap-1 text-[11px] whitespace-nowrap ${PAIEMENT_STYLE[it.paiement] || "text-slate-500"}`}>
          <CreditCard size={11} />{it.paiement_libelle}
        </span>
      )}
    </span>
  );
}

function Ligne({ label, valeur, mono, copier = true }) {
  return (
    <div className="grid grid-cols-[150px_1fr_auto] items-center gap-3 py-2 border-b border-[#EEF2F7] last:border-0 max-sm:grid-cols-[1fr_auto]">
      <div className="text-[12px] text-[#64748B] max-sm:col-span-2">{label}</div>
      <div className={`text-[13.5px] text-[#0F2B5B] break-all ${mono ? "font-mono tabular-nums" : ""}`}>{valeur || <span className="text-[#94A3B8]">—</span>}</div>
      <div>{copier && valeur ? <BoutonCopier texte={valeur} /> : null}</div>
    </div>
  );
}

function Bloc({ icone: Icone, titre, etape, toutCopier, children, aside }) {
  return (
    <section className="bg-white border border-[#E2E8F0] rounded-xl">
      <header className="flex items-center justify-between gap-3 px-5 py-3.5 border-b border-[#EEF2F7] flex-wrap">
        <div className="flex items-center gap-2.5">
          <span className="grid place-items-center h-7 w-7 rounded-lg bg-[#EFF6FF] text-[#1D4ED8]"><Icone size={15} /></span>
          <div>
            {etape && <div className="text-[10.5px] uppercase tracking-[0.16em] text-[#94A3B8]">{etape}</div>}
            <h3 className="text-[14.5px] font-semibold text-[#0F2B5B] leading-tight">{titre}</h3>
          </div>
        </div>
        <div className="flex items-center gap-2">{aside}{toutCopier && <BoutonCopier texte={toutCopier} libelle="Tout copier" />}</div>
      </header>
      <div className="px-5 py-2">{children}</div>
    </section>
  );
}

function Fiche({ reference, libelles, onRetour, onChange }) {
  const [d, setD] = useState(null);
  const [err, setErr] = useState("");
  const [info, setInfo] = useState("");
  const [occupe, setOccupe] = useState(false);
  const [refus, setRefus] = useState(null);
  const [note, setNote] = useState("");

  const charger = useCallback(async () => {
    setErr("");
    try { setD((await axios.get(`${B}/${reference}`)).data); } catch (e) { setErr(erreur(e)); }
  }, [reference]);
  useEffect(() => { charger(); }, [charger]);

  const statut = async (s, texte = "", notifier = true) => {
    setOccupe(true); setErr(""); setInfo("");
    try {
      const { data } = await axios.post(`${B}/${reference}/statut`, { statut: s, note: texte, notifier });
      const inv = data.invitation?.envoyee ? ` · invitation à l'espace MetaConnect envoyée à ${data.invitation.email}` : "";
      setInfo(`Statut mis à jour : ${libelles[s]}${data.email_envoye ? " · e-mail envoyé au demandeur" : ""}${inv}.`);
      setRefus(null); setNote("");
      await charger(); onChange();
    } catch (e) { setErr(erreur(e)); } finally { setOccupe(false); }
  };
  const controler = async () => {
    setOccupe(true); setErr(""); setInfo("");
    try {
      const { data } = await axios.post(`${B}/${reference}/controle`);
      setInfo(data.annuaire_indisponible ? "L'Annuaire santé ne répond pas. Réessayez dans quelques minutes."
        : data.publiee ? "La boîte est visible dans l'Annuaire santé : demande passée en « Publiée »."
          : "La boîte n'apparaît pas encore dans l'Annuaire santé.");
      await charger(); onChange();
    } catch (e) { setErr(erreur(e)); } finally { setOccupe(false); }
  };

  const inviter = async () => {
    setOccupe(true); setErr(""); setInfo("");
    try {
      const { data } = await axios.post(`${B}/${reference}/invitation`);
      setInfo(`Invitation à l'espace MetaConnect envoyée à ${data.email}.`);
      await charger();
    } catch (e) { setErr(erreur(e)); } finally { setOccupe(false); }
  };

  const vu = async () => {
    setOccupe(true); setErr(""); setInfo("");
    try {
      await axios.post(`${B}/${reference}/vu`);
      setInfo("Modifications marquées comme reportées dans Maincare.");
      await charger(); onChange();
    } catch (e) { setErr(erreur(e)); } finally { setOccupe(false); }
  };

  if (!d) {
    return (
      <div className="bg-white border border-[#E2E8F0] rounded-xl p-8 text-[13.5px] text-[#64748B]">
        {err || "Chargement de la demande…"}
      </div>
    );
  }
  const ej = d.entite_juridique || {}, eg = d.etablissement || {}, b = d.boite || {};
  const users = d.utilisateurs || [];
  const lienPiece = (u) => `${B}/${d.reference}/pieces/${u.piece_id}?t=${encodeURIComponent(jeton())}`;
  const site = d.source === "site";
  const aEj = Boolean(d.entite_juridique);
  const txtStructure = [
    aEj ? `Entité juridique\t${ej.type_id} ${ej.id}\t${ej.nom || ""}` : null,
    `Établissement\t${eg.type_id} ${eg.id}\t${eg.nom || ""}${eg.adresse ? `\t${eg.adresse}` : ""}`,
  ].filter(Boolean).join("\n");
  const txtBoite = [`Adresse\t${b.adresse}`, `Type\t${b.type}`, `Description\t${b.description || ""}`,
    `Liste rouge\t${b.liste_rouge ? "Oui" : "Non"}`].join("\n");
  const txtUsers = users.map((u) => [u.nom, u.prenom, u.type_libelle, u.id_complet, u.email, u.telephone,
    u.profession, u.droit].join("\t")).join("\n");
  const ouverte = !["publiee", "refusee", "annulee"].includes(d.statut);
  const aFaire = OUVERTS.includes(d.statut);

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <button type="button" onClick={onRetour} className="inline-flex items-center gap-1.5 text-[13px] text-[#475569] hover:text-[#0F2B5B]">
          <ChevronLeft size={15} /> Toutes les demandes
        </button>
        <div className="flex items-center gap-2 flex-wrap">
          <span className="font-mono text-[13px] text-[#0F2B5B]">{d.reference}</span>
          <Provenance it={d} grand />
          <Pastille statut={d.statut} libelles={libelles} />
          <span className="text-[12px] text-[#64748B]">reçue le {date(d.cree_le)}</span>
        </div>
      </div>

      {(err || info) && (
        <div role="status" className={`rounded-lg border px-4 py-2.5 text-[13px] ${err ? "border-red-200 bg-red-50 text-red-700" : "border-emerald-200 bg-emerald-50 text-emerald-800"}`}>
          {err || info}
        </div>
      )}

      <div className="bg-white border border-[#E2E8F0] rounded-xl px-5 py-4 flex items-center justify-between gap-3 flex-wrap">
        <div className="text-[13px] text-[#475569] max-w-xl">
          {d.statut === "creee"
            ? `Boîte créée chez Maincare. Le contrôle automatique la passe en « Publiée » dès qu'elle apparaît dans l'Annuaire santé (toutes les 6 h).${site ? "" : " Le responsable a reçu une invitation pour activer son espace MetaConnect."}`
            : d.statut === "publiee" ? (site ? "Boîte publiée : la commande est active." : `Publiée le ${date(d.publiee_le)}. Les pièces sont supprimées 30 jours plus tard.`)
              : d.statut === "refusee" ? `Refusée le ${date(d.refusee_le)} — ${d.motif_refus || "sans motif"}.`
                : d.statut === "annulee" ? "Commande annulée (abonnement arrêté) : rien à créer chez Maincare."
                  : d.statut === "attente_paiement" ? "Le paiement Stripe n'est pas encore confirmé. Attendez-le avant de créer la boîte chez Maincare."
                    : `Recopiez les trois blocs ci-dessous dans Maincare, dans cet ordre, puis indiquez que la boîte est créée.${site ? "" : " Le responsable recevra alors son invitation à l'espace MetaConnect."}`}
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {aFaire && (
            <button type="button" disabled={occupe} onClick={() => statut("en_cours")}
              className="px-3.5 py-2 rounded-lg border border-[#E2E8F0] text-[13px] text-[#0F2B5B] hover:border-indigo-400 disabled:opacity-50">
              Je m'en occupe
            </button>
          )}
          {(aFaire || d.statut === "en_cours") && (
            <button type="button" disabled={occupe} onClick={() => statut("creee")}
              className="px-3.5 py-2 rounded-lg bg-[#0F2B5B] text-white text-[13px] font-medium hover:bg-[#16397A] disabled:opacity-50">
              Créée chez Maincare
            </button>
          )}
          {d.statut === "creee" && (
            <>
              <button type="button" disabled={occupe} onClick={controler}
                className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-lg bg-[#0F2B5B] text-white text-[13px] font-medium hover:bg-[#16397A] disabled:opacity-50">
                <Radar size={14} /> Contrôler la publication
              </button>
              <button type="button" disabled={occupe} onClick={() => statut("publiee")}
                className="px-3.5 py-2 rounded-lg border border-[#E2E8F0] text-[13px] text-[#0F2B5B] hover:border-emerald-400 disabled:opacity-50">
                Marquer publiée
              </button>
            </>
          )}
          {ouverte && site && (
            <Link to={d.lien_gestion || "/admin/mssante"}
              className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-lg border border-[#E2E8F0] text-[13px] text-[#475569] hover:border-sky-400">
              <ExternalLink size={13} /> Contrat, accès, e-mail
            </Link>
          )}
          {ouverte && !site && (
            <button type="button" disabled={occupe} onClick={() => setRefus(true)}
              className="px-3.5 py-2 rounded-lg border border-[#E2E8F0] text-[13px] text-red-700 hover:border-red-300 disabled:opacity-50">
              Refuser
            </button>
          )}
          {!ouverte && d.statut !== "annulee" && (
            <button type="button" disabled={occupe} onClick={() => statut("recue", "réouverture", false)}
              className="px-3.5 py-2 rounded-lg border border-[#E2E8F0] text-[13px] text-[#475569] hover:border-sky-400 disabled:opacity-50">
              Rouvrir
            </button>
          )}
        </div>
        {refus && (
          <div className="w-full flex items-end gap-2 flex-wrap pt-2 border-t border-[#EEF2F7]">
            <label className="flex-1 min-w-[240px] text-[12px] text-[#64748B]">
              Motif envoyé au demandeur
              <input id="motif-refus" value={note} onChange={(e) => setNote(e.target.value)} maxLength={500}
                placeholder="Ex. : l'identifiant du collaborateur ne correspond à aucune carte"
                className="mt-1 w-full rounded-lg border border-[#E2E8F0] px-3 py-2 text-[13.5px] text-[#0F2B5B] focus:outline-none focus:border-sky-400" />
            </label>
            <button type="button" disabled={occupe || !note.trim()} onClick={() => statut("refusee", note)}
              className="px-3.5 py-2 rounded-lg bg-red-600 text-white text-[13px] font-medium disabled:opacity-40">Confirmer le refus</button>
            <button type="button" onClick={() => { setRefus(null); setNote(""); }} className="px-3 py-2 text-[13px] text-[#475569]">Annuler</button>
          </div>
        )}
      </div>

      {d.modifs_en_attente && (
        <div className="flex items-center justify-between gap-3 flex-wrap rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-[13px] text-amber-900">
          <span className="flex gap-2.5"><AlertTriangle size={16} className="shrink-0 mt-0.5" />
            Le professionnel ou le support a modifié les utilisateurs depuis la dernière saisie : reportez les changements dans Maincare (voir l'historique).</span>
          <button type="button" disabled={occupe} onClick={vu}
            className="px-3 py-1.5 rounded-md border border-amber-300 bg-white text-[12.5px] text-amber-900 hover:border-amber-500 disabled:opacity-50">
            Modifications reportées
          </button>
        </div>
      )}
      {site && !eg.verifiee && d.statut !== "annulee" && (
        <div className="flex gap-2.5 rounded-lg border border-sky-200 bg-sky-50 px-4 py-3 text-[13px] text-sky-900">
          <Globe size={16} className="shrink-0 mt-0.5" />
          Commande passée sur le site : la structure a été saisie par le professionnel, sans contrôle dans l'Annuaire santé. Vérifiez l'identifiant avant de créer la boîte.
        </div>
      )}
      {d.statut === "structure_a_verifier" && (
        <div className="flex gap-2.5 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-[13px] text-amber-900">
          <AlertTriangle size={16} className="shrink-0 mt-0.5" />
          L'Annuaire santé ne répondait pas lors de la demande : vérifiez que l'établissement existe et qu'il n'a pas déjà une boîte avant de le créer.
        </div>
      )}
      {d.option_sous_domaine && (
        <div className="flex gap-2.5 rounded-lg border border-violet-200 bg-violet-50 px-4 py-3 text-[13px] text-violet-900">
          <FileText size={16} className="shrink-0 mt-0.5" />
          Option sous-domaine dédié acceptée : <strong className="font-mono">{d.option_sous_domaine.nom}</strong> — {d.option_sous_domaine.prix_ht} € HT à facturer. Le sous-domaine doit être créé avant la boîte.
        </div>
      )}

      <Bloc icone={Building2} etape="Étape 1 · Structures" titre={aEj ? "Entité juridique et établissement" : "Structure"} toutCopier={txtStructure}
        aside={eg.verifiee ? <span className="inline-flex items-center gap-1 text-[11.5px] text-emerald-700"><ShieldCheck size={13} />Vérifié dans l'Annuaire santé</span> : null}>
        {aEj && (
          <>
            <Ligne label="EJ · type d'identifiant" valeur={ej.type_id} copier={false} />
            <Ligne label="EJ · identifiant" valeur={ej.id} mono />
            <Ligne label="EJ · nom" valeur={ej.nom} />
          </>
        )}
        <Ligne label="EG · type d'identifiant" valeur={eg.type_id} copier={false} />
        <Ligne label="EG · identifiant" valeur={eg.id} mono />
        <Ligne label="EG · nom" valeur={eg.nom} />
        <Ligne label="EG · adresse" valeur={eg.adresse} />
      </Bloc>

      <Bloc icone={Mail} etape="Étape 2 · Boîte aux lettres" titre={b.adresse} toutCopier={txtBoite}>
        <Ligne label="Partie avant @" valeur={b.local} mono />
        <Ligne label="Domaine" valeur={b.domaine} mono />
        <Ligne label="Adresse complète" valeur={b.adresse} mono />
        <Ligne label="Type" valeur={b.type} copier={false} />
        <Ligne label="Description" valeur={b.description} />
        <Ligne label="Liste rouge" valeur={b.liste_rouge ? "Oui — ne pas publier" : "Non — publiée"} copier={false} />
      </Bloc>

      <Bloc icone={Users} etape="Étape 3 · Utilisateurs" titre={`${users.length} utilisateur${users.length > 1 ? "s" : ""}`} toutCopier={txtUsers}>
        <div className="divide-y divide-[#EEF2F7]">
          {users.map((u, i) => (
            <div key={i} className="py-3">
              <div className="flex items-center justify-between gap-2 flex-wrap mb-1">
                <div className="text-[13.5px] font-semibold text-[#0F2B5B]">
                  {u.prenom} {u.nom}
                  <span className={`ml-2 text-[11px] font-medium px-2 py-0.5 rounded-full ${u.role === "responsable" ? "bg-[#0F2B5B] text-white" : "bg-slate-100 text-slate-600"}`}>
                    {u.role === "responsable" ? "Responsable" : u.droit}
                  </span>
                  {u.carte && <span className="ml-2 text-[11px] text-[#64748B]">carte : {u.carte}</span>}
                </div>
                {u.piece_id && !d.pieces_purgees_le && (
                  <a href={lienPiece(u)} target="_blank" rel="noopener noreferrer"
                    className="inline-flex items-center gap-1 text-[12px] text-[#1D4ED8] hover:underline">
                    <FileText size={13} /> {u.piece_nom || "Pièce justificative"}
                  </a>
                )}
              </div>
              <Ligne label="Nom" valeur={u.nom} />
              <Ligne label="Prénom" valeur={u.prenom} />
              <Ligne label="Type d'ID" valeur={`${u.type_libelle} (${u.type_id})`} copier={false} />
              {u.a_convertir && (
                <div className="my-1 flex gap-2 rounded-md bg-amber-50 px-3 py-2 text-[12px] text-amber-900">
                  <AlertTriangle size={13} className="shrink-0 mt-0.5" />
                  Maincare n'accepte pas ce type tel quel : retrouvez le RPPS ou l'ADELI de la personne, ou saisissez-la par e-mail.
                </div>
              )}
              <Ligne label="ID Maincare" valeur={u.id_complet} mono />
              <Ligne label="Email" valeur={u.email} mono />
              <Ligne label="Téléphone" valeur={u.telephone} mono />
              <Ligne label="Profession" valeur={u.profession} />
              <Ligne label="Droit" valeur={u.droit} copier={false} />
            </div>
          ))}
        </div>
      </Bloc>

      <div className="grid gap-5 md:grid-cols-2">
        {site ? (
          <Bloc icone={CreditCard} titre="Commande et paiement">
            <Ligne label="Provenance" valeur="Site MetaConnect · Commander ma boîte" copier={false} />
            <Ligne label="Paiement" valeur={d.paiement_libelle} copier={false} />
            <Ligne label="Formule" valeur={d.formule} copier={false} />
            <Ligne label="Email du client" valeur={d.email_abonnement} mono />
            <Ligne label="Contrat accepté le" valeur={d.contrat?.accepte_le ? `${date(d.contrat.accepte_le)} (${d.contrat.version || "—"})` : ""} copier={false} />
            <Ligne label="Créée chez Maincare le" valeur={d.maincare_created_at} copier={false} />
            <Ligne label="Note interne" valeur={d.admin_note} copier={false} />
            <Ligne label="Dernier contrôle annuaire" valeur={date(d.dernier_controle_le)} copier={false} />
          </Bloc>
        ) : (
          <Bloc icone={ShieldCheck} titre="Demandeur et validation">
            <Ligne label="Provenance" valeur="Lien OhMyVue (client abonné)" copier={false} />
            <Ligne label="Email de l'abonnement" valeur={d.email_abonnement} mono />
            <Ligne label="Code confirmé le" valeur={date(d.validation?.code_confirme_le)} copier={false} />
            <Ligne label="Habilitation cochée" valeur={d.validation?.habilitation ? "Oui" : "Non"} copier={false} />
            <Ligne label="Dernier contrôle annuaire" valeur={date(d.dernier_controle_le)} copier={false} />
            <Ligne label="Espace MetaConnect" copier={false} valeur={
              d.espace_active_le ? `Activé le ${date(d.espace_active_le)}`
                : d.invitation_envoyee_le ? `Invitation envoyée le ${date(d.invitation_envoyee_le)} à ${d.invitation_email}`
                  : "Invitation envoyée au passage « Créée chez Maincare »"} />
            {["creee", "publiee"].includes(d.statut) && !d.espace_active_le && (
              <div className="py-2">
                <button type="button" disabled={occupe} onClick={inviter} data-testid="admin-inviter"
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md border border-[#E2E8F0] text-[12.5px] text-[#0F2B5B] hover:border-sky-400 disabled:opacity-50">
                  <Mail size={13} /> {d.invitation_envoyee_le ? "Renvoyer l'invitation" : "Envoyer l'invitation"}
                </button>
              </div>
            )}
          </Bloc>
        )}
        <Bloc icone={History} titre="Historique">
          <ol className="py-1">
            {(d.historique || []).slice().reverse().map((h, i) => (
              <li key={i} className="py-1.5 text-[12.5px] text-[#475569] border-b border-[#EEF2F7] last:border-0">
                <span className="tabular-nums text-[#94A3B8] mr-2">{date(h.le)}</span>{h.action}
                {h.par && <span className="text-[#94A3B8]"> · {h.par}</span>}
              </li>
            ))}
          </ol>
        </Bloc>
      </div>
    </div>
  );
}

export default function AdminDemandesMss() {
  const [liste, setListe] = useState({ items: [], compteurs: {}, libelles: {}, verifications: [], par_provenance: {}, total: 0 });
  const [filtre, setFiltre] = useState("a_traiter");
  const [provenance, setProvenance] = useState("");
  const [q, setQ] = useState("");
  const [charge, setCharge] = useState(true);
  const [err, setErr] = useState("");
  const [ouverte, setOuverte] = useState(() => new URLSearchParams(window.location.search).get("ref"));

  const charger = useCallback(async () => {
    setCharge(true); setErr("");
    try { setListe((await axios.get(B, { params: { statut: filtre, q, provenance } })).data); }
    catch (e) { setErr(erreur(e)); }
    finally { setCharge(false); }
  }, [filtre, q, provenance]);
  useEffect(() => { const t = setTimeout(charger, q ? 300 : 0); return () => clearTimeout(t); }, [charger, q]);

  const ouvrir = (ref) => {
    setOuverte(ref);
    const url = new URL(window.location.href);
    if (ref) url.searchParams.set("ref", ref); else url.searchParams.delete("ref");
    window.history.replaceState(null, "", url);
    window.scrollTo(0, 0);
  };
  const compte = (k) => (k ? liste.compteurs[k] || 0 : liste.total || 0);
  const totalProv = useMemo(() => Object.values(liste.par_provenance || {}).reduce((a, b) => a + b, 0), [liste.par_provenance]);
  const compteProv = (k) => (k ? liste.par_provenance?.[k] || 0 : totalProv);
  const exporter = () => {
    const p = new URLSearchParams({ t: jeton(), statut: filtre, provenance });
    const url = `${API}/admin/demandes-mss-export.csv?${p.toString()}`;
    window.open(url, "_blank", "noopener,noreferrer");
  };
  const traiterVerif = async (id, statut) => {
    try { await axios.post(`${API}/admin/mss-verifications/${id}/statut`, { statut }); charger(); }
    catch (e) { setErr(erreur(e)); }
  };

  return (
    <AdminPageShell title="Demandes MSSanté" tab="demandes-mss" eyebrow="Commandes de boîtes"
      subtitle="Toutes les boîtes commandées, sur le site ou par le lien OhMyVue, à recopier dans Maincare" seoCanonical="/admin/demandes-mss">
      {ouverte ? (
        <Fiche reference={ouverte} libelles={liste.libelles} onRetour={() => ouvrir(null)} onChange={charger} />
      ) : (
        <div className="space-y-5">
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <div className="inline-flex rounded-lg border border-[#E2E8F0] bg-white p-0.5 flex-wrap" role="group" aria-label="Filtrer par provenance">
              {PROVENANCES.map(([k, l]) => (
                <button key={k || "toutes"} type="button" aria-pressed={provenance === k} onClick={() => setProvenance(k)}
                  className={`px-3 py-1.5 rounded-md text-[12.5px] transition ${provenance === k ? "bg-[#EFF6FF] text-[#0F2B5B] font-medium" : "text-[#475569] hover:text-[#0F2B5B]"}`}>
                  {l} <span className="tabular-nums opacity-60">{compteProv(k)}</span>
                </button>
              ))}
            </div>
            <Link to="/admin/mssante" className="inline-flex items-center gap-1.5 text-[12.5px] text-[#475569] hover:text-[#0F2B5B]">
              <ExternalLink size={13} /> Gestion avancée : contrats, comptes pros, accès
            </Link>
          </div>
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <div className="flex items-center gap-1.5 flex-wrap" role="tablist" aria-label="Filtrer par statut">
              {FILTRES.map(([k, l]) => (
                <button key={k || "toutes"} type="button" role="tab" aria-selected={filtre === k} onClick={() => setFiltre(k)}
                  className={`px-3 py-1.5 rounded-full border text-[12.5px] transition ${filtre === k ? "bg-[#0F2B5B] border-[#0F2B5B] text-white" : "bg-white border-[#E2E8F0] text-[#475569] hover:border-sky-400"}`}>
                  {l} <span className="tabular-nums opacity-70">{compte(k)}</span>
                </button>
              ))}
            </div>
            <div className="flex items-center gap-2">
              <label className="relative">
                <span className="sr-only">Rechercher</span>
                <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#94A3B8]" />
                <input id="recherche-demandes" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Référence, adresse, SIRET, nom…"
                  className="w-64 max-sm:w-full rounded-lg border border-[#E2E8F0] bg-white pl-8 pr-8 py-2 text-[13px] text-[#0F2B5B] focus:outline-none focus:border-sky-400" />
                {q && <button type="button" onClick={() => setQ("")} aria-label="Effacer" className="absolute right-2 top-1/2 -translate-y-1/2 text-[#94A3B8]"><X size={14} /></button>}
              </label>
              <button type="button" onClick={charger} title="Actualiser" className="grid place-items-center h-9 w-9 rounded-lg border border-[#E2E8F0] bg-white text-[#475569] hover:border-sky-400">
                <RefreshCw size={14} className={charge ? "animate-spin" : ""} />
              </button>
              <button type="button" onClick={exporter} className="inline-flex items-center gap-1.5 h-9 px-3 rounded-lg border border-[#E2E8F0] bg-white text-[13px] text-[#0F2B5B] hover:border-sky-400">
                <Download size={14} /> Export CSV
              </button>
            </div>
          </div>

          {err && <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-2.5 text-[13px] text-red-700">{err}</div>}

          <div className="bg-white border border-[#E2E8F0] rounded-xl overflow-x-auto">
            <table className="w-full text-left text-[13px]">
              <thead>
                <tr className="text-[11.5px] uppercase tracking-[0.08em] text-[#64748B] border-b border-[#EEF2F7]">
                  <th className="px-4 py-3 font-medium">Référence</th>
                  <th className="px-4 py-3 font-medium">Provenance</th>
                  <th className="px-4 py-3 font-medium">Reçue le</th>
                  <th className="px-4 py-3 font-medium">Adresse demandée</th>
                  <th className="px-4 py-3 font-medium">Établissement</th>
                  <th className="px-4 py-3 font-medium">Responsable</th>
                  <th className="px-4 py-3 font-medium text-right">Util.</th>
                  <th className="px-4 py-3 font-medium">Statut</th>
                </tr>
              </thead>
              <tbody>
                {liste.items.map((it) => (
                  <tr key={it.reference} onClick={() => ouvrir(it.reference)}
                    className="border-b border-[#EEF2F7] last:border-0 hover:bg-[#F8FAFC] cursor-pointer">
                    <td className="px-4 py-3 font-mono text-[#0F2B5B] whitespace-nowrap">
                      <button type="button" className="hover:underline focus:outline-none focus-visible:underline" onClick={(e) => { e.stopPropagation(); ouvrir(it.reference); }}>{it.reference}</button>
                    </td>
                    <td className="px-4 py-3"><Provenance it={it} /></td>
                    <td className="px-4 py-3 tabular-nums text-[#475569] whitespace-nowrap">{date(it.cree_le)}</td>
                    <td className="px-4 py-3 font-mono text-[#0F2B5B]">
                      {it.adresse}{it.option_sous_domaine && <span className="ml-1.5 text-[10.5px] font-sans px-1.5 py-0.5 rounded bg-violet-50 text-violet-700">dédié</span>}
                    </td>
                    <td className="px-4 py-3 text-[#0F2B5B]">
                      {it.etablissement}
                      <div className="text-[11.5px] text-[#64748B] font-mono">{it.etablissement_id}{it.verifiee === false && <span className="ml-1 font-sans text-amber-700">· non vérifié</span>}</div>
                    </td>
                    <td className="px-4 py-3 text-[#475569]">{it.responsable}</td>
                    <td className="px-4 py-3 text-right tabular-nums text-[#475569]">{it.nb_utilisateurs}</td>
                    <td className="px-4 py-3">
                      <Pastille statut={it.statut} libelles={liste.libelles} />
                      {it.modifs_en_attente && <div className="mt-1 text-[11px] text-amber-700 whitespace-nowrap">modifications à reporter</div>}
                    </td>
                  </tr>
                ))}
                {!liste.items.length && (
                  <tr><td colSpan={8} className="px-4 py-10 text-center text-[#64748B]">
                    {charge ? "Chargement…" : "Aucune demande pour ce filtre."}
                  </td></tr>
                )}
              </tbody>
            </table>
          </div>

          {liste.verifications.length > 0 && (
            <section className="bg-white border border-amber-200 rounded-xl">
              <header className="px-5 py-3.5 border-b border-amber-100 flex items-center gap-2">
                <AlertTriangle size={15} className="text-amber-600" />
                <h3 className="text-[14px] font-semibold text-[#0F2B5B]">Structures introuvables signalées ({liste.verifications.length})</h3>
              </header>
              <ul>
                {liste.verifications.map((v) => (
                  <li key={v.id} className="px-5 py-3 border-b border-[#EEF2F7] last:border-0 flex items-center justify-between gap-3 flex-wrap text-[13px]">
                    <div>
                      <span className="font-mono text-[#0F2B5B]">{v.type_id} {v.identifiant}</span>
                      <span className="text-[#475569]"> · {v.nom || "nom non précisé"} · {v.email}</span>
                      <span className="text-[#94A3B8]"> · {date(v.cree_le)}</span>
                    </div>
                    <div className="flex gap-2">
                      <BoutonCopier texte={v.identifiant} />
                      <button type="button" onClick={() => traiterVerif(v.id, "traitee")} className="px-2.5 py-1 rounded-md border border-[#E2E8F0] text-[12px] text-emerald-700 hover:border-emerald-300">Traitée</button>
                      <button type="button" onClick={() => traiterVerif(v.id, "rejetee")} className="px-2.5 py-1 rounded-md border border-[#E2E8F0] text-[12px] text-[#475569] hover:border-slate-400">Rejeter</button>
                    </div>
                  </li>
                ))}
              </ul>
            </section>
          )}
        </div>
      )}
    </AdminPageShell>
  );
}
